import { Component, inject, signal, computed, Output, EventEmitter, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CompanyApplication } from '@core/models/dynamic-company.models';
import { AuthService } from '@core/services/auth-new.service';
import { CompanyService } from '@core/services/company.service';
import { DropdownComponent } from '@shared/components/ui/dropdown';

@Component({
  selector: 'app-company-switcher',
  standalone: true,
  imports: [CommonModule, DropdownComponent],
  templateUrl: './company-switcher.component.html',
  styleUrls: ['./company-switcher.component.scss']
})
export class CompanySwitcherComponent {
  private readonly authService = inject(AuthService);
  private readonly companyService = inject(CompanyService);

  @Output() dropdownOpenChange = new EventEmitter<boolean>();

  // State
  isDropdownOpen = signal(false);
  availableCompanies = signal<CompanyApplication[]>([]);

  // Reactive: updates automatically when authService switches company
  readonly currentCompanyName = computed(
    () => this.authService.currentCompany() || localStorage.getItem('currentCompany') || 'unity'
  );

  // Computed
  currentCompany = computed(() => {
    const companies = this.availableCompanies();
    const currentName = this.currentCompanyName();
    return companies.find(c => c.name === currentName) || companies[0] || null;
  });

  otherCompanies = computed(() => {
    const companies = this.availableCompanies();
    const currentName = this.currentCompanyName();

    // Filter out current company and deduplicate by name
    const filtered = companies.filter(c => c.name !== currentName);

    // Deduplicate: keep only first occurrence of each company name
    const uniqueCompanies = filtered.reduce((acc, company) => {
      if (!acc.some(c => c.name === company.name)) {
        acc.push(company);
      }
      return acc;
    }, [] as CompanyApplication[]);

    return uniqueCompanies;
  });

  constructor() {
    this.loadAvailableCompanies();
    effect(() => {
      this.dropdownOpenChange.emit(this.isDropdownOpen());
    });
  }

  /**
   * Load available companies from localStorage
   */
  private loadAvailableCompanies(): void {
    try {
      const applicationsJson = localStorage.getItem('Applications');
      if (applicationsJson) {
        const applications: CompanyApplication[] = JSON.parse(applicationsJson);
        const availableApps = applications.filter(app => app.isAvailable);

        // Deduplicate companies by name (keep first occurrence)
        const uniqueApps = availableApps.reduce((acc, app) => {
          if (!acc.some(a => a.name === app.name)) {
            acc.push(app);
          }
          return acc;
        }, [] as CompanyApplication[]);

        this.availableCompanies.set(uniqueApps);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  /**
   * Switch to selected company
   */
  async onCompanyClick(company: CompanyApplication): Promise<void> {
    if (company.name === this.currentCompanyName()) {
      this.isDropdownOpen.set(false);
      return;
    }

    console.log('🔄 Company switch requested:', {
      from: this.currentCompanyName(),
      to: company.name,
      company: company
    });

    try {
      // Close dropdown before switching
      this.isDropdownOpen.set(false);

      // Use CompanyService to handle the complete company switch
      // This will update user details, tokens, AuthService state, and reload the page
      await this.companyService.changeCompany(company);

      console.log(`✅ Successfully switched to company: ${company.name}`);
    } catch (error) {
      console.error('❌ Error switching company:', error);
      this.isDropdownOpen.set(false);
    }
  }

  /**
   * Toggle dropdown open/close
   */
  toggleDropdown(): void {
    this.isDropdownOpen.update(open => !open);
  }

  /**
   * Get company logo or fallback icon
   */
  getCompanyLogo(company: CompanyApplication): string | null {
    return company.logo || null;
  }

  /**
   * Get company icon or fallback
   */
  getCompanyIcon(company: CompanyApplication): string | null {
    return company.icon;
  }

  /**
   * Check if company has logo
   */
  hasLogo(company: CompanyApplication): boolean {
    return !!company.logo;
  }

  /**
   * Check if company has icon
   */
  hasIcon(company: CompanyApplication): boolean {
    return !!company.icon;
  }

  /**
   * Get fallback image based on company name
   */
  getFallbackImage(companyName: string): string | null {
    const fallbackImages: Record<string, string> = {
      unity: 'assets/images/unity-icon.webp',
      mingle: 'assets/headers-image/mingle-icon.svg'
    };
    return fallbackImages[companyName] || null;
  }
}
